/**
 * Altitude Computation Logic
 * 
 * Computes altitude level (1-5) from three structural dimensions:
 * - Time Horizon: When would we see meaningful impact?
 * - Work Size: How much effort is involved?
 * - Impact Surface: What is the breadth of effect?
 * 
 * IMPORTANT: Altitude is NOT a prediction or a label.
 * Altitude IS the rounded average of the dimension scores.
 * The system does structural thinking on behalf of the user.
 */

import type { 
  AltitudeLevel,
  ALTITUDE_LEVEL_NAMES,
} from '../types/prioritization/itemLifecycle';
import type { 
  AltitudeScopingInputs, 
  TimeHorizon, 
  WorkSize, 
  ImpactSurface,
  ComputedAltitude,
} from '../types/prioritization/leveling';

// Re-export for convenience
export { ALTITUDE_LEVEL_NAMES } from '../types/prioritization/itemLifecycle';

// ============================================================================
// Dimension Score Mappings (Each dimension maps to 1-5)
// ============================================================================

/**
 * Time horizon scores (1-5)
 * Higher score = longer horizon = higher altitude
 */
export const TIME_HORIZON_SCORES: Record<TimeHorizon, AltitudeLevel> = {
  'days': 1,
  'weeks': 2,
  '1-3_months': 3,
  '3-12_months': 4,
  '1+_years': 5,
};

/**
 * Work size scores (1-5)
 * Higher score = larger effort = higher altitude
 */
export const WORK_SIZE_SCORES: Record<WorkSize, AltitudeLevel> = {
  '1-2_weeks': 1,
  '3-8_weeks': 2,
  '9-26_weeks': 3,
  '27-52_weeks': 4,
  '52+_weeks': 5,
};

/**
 * Impact surface scores (1-5)
 * Higher score = broader impact = higher altitude
 */
export const IMPACT_SURFACE_SCORES: Record<ImpactSurface, AltitudeLevel> = {
  'one_screen': 1,
  'one_workflow': 2,
  'multiple_workflows': 3,
  'multiple_products': 4,
  'company_wide': 5,
};

// ============================================================================
// Altitude Computation (The Core Logic)
// ============================================================================

/**
 * Compute the altitude level from scoping inputs
 * 
 * The altitude IS the rounded average of the provided dimension scores.
 * This is not a prediction - it is the canonical altitude.
 * 
 * @param inputs - The three dimension inputs
 * @returns AltitudeLevel (1-5), or undefined if no inputs provided
 */
export function computeAltitudeLevel(inputs: AltitudeScopingInputs): AltitudeLevel | undefined {
  const scores: number[] = [];
  
  if (inputs.timeHorizon) {
    scores.push(TIME_HORIZON_SCORES[inputs.timeHorizon]);
  }
  if (inputs.workSize) {
    scores.push(WORK_SIZE_SCORES[inputs.workSize]);
  }
  if (inputs.impactSurface) {
    scores.push(IMPACT_SURFACE_SCORES[inputs.impactSurface]);
  }
  
  // Return undefined if no inputs - altitude must be explicitly set via scoping questions
  if (scores.length === 0) {
    return undefined;
  }
  
  // Compute average and round to nearest integer (1-5)
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const rounded = Math.round(avg);
  
  // Clamp to valid range
  return Math.max(1, Math.min(5, rounded)) as AltitudeLevel;
}

/**
 * Compute altitude with full details including confidence and reasoning
 */
export function computeAltitude(inputs: AltitudeScopingInputs): ComputedAltitude {
  const dimensionScores: ComputedAltitude['dimensionScores'] = {};
  const scores: number[] = [];
  
  if (inputs.timeHorizon) {
    dimensionScores.timeHorizon = TIME_HORIZON_SCORES[inputs.timeHorizon];
    scores.push(dimensionScores.timeHorizon);
  }
  if (inputs.workSize) {
    dimensionScores.workSize = WORK_SIZE_SCORES[inputs.workSize];
    scores.push(dimensionScores.workSize);
  }
  if (inputs.impactSurface) {
    dimensionScores.impactSurface = IMPACT_SURFACE_SCORES[inputs.impactSurface];
    scores.push(dimensionScores.impactSurface);
  }
  
  const level = computeAltitudeLevel(inputs);
  const confidence = determineConfidence(inputs, scores);
  const reasoning = generateReasoning(inputs, level);
  
  return {
    level,
    confidence,
    reasoning,
    dimensionScores,
  };
}

/**
 * Determine confidence based on input completeness and consistency
 */
function determineConfidence(
  inputs: AltitudeScopingInputs, 
  scores: number[]
): 'high' | 'medium' | 'low' {
  const inputCount = scores.length;
  
  if (inputCount === 0) return 'low';
  if (inputCount === 1) return 'low';
  if (inputCount === 2) return 'medium';
  
  // All three inputs provided - check for consistency
  const max = Math.max(...scores);
  const min = Math.min(...scores);
  const spread = max - min;
  
  // If inputs are consistent (spread <= 1), high confidence
  // If spread is 2, medium confidence
  // If spread is 3+, low confidence (conflicting signals)
  if (spread <= 1) return 'high';
  if (spread === 2) return 'medium';
  return 'low';
}

/**
 * Generate reasoning text for the computed altitude
 */
function generateReasoning(inputs: AltitudeScopingInputs, level: AltitudeLevel): string {
  const parts: string[] = [];
  
  const levelNames: Record<AltitudeLevel, string> = {
    5: 'Strategic',
    4: 'Program',
    3: 'Capability',
    2: 'Delivery',
    1: 'Tactical',
  };
  
  if (inputs.timeHorizon) {
    const horizonLabels: Record<TimeHorizon, string> = {
      'days': 'days',
      'weeks': 'weeks',
      '1-3_months': '1-3 months',
      '3-12_months': '3-12 months',
      '1+_years': '1+ years',
    };
    parts.push(`time horizon: ${horizonLabels[inputs.timeHorizon]}`);
  }
  
  if (inputs.workSize) {
    const sizeLabels: Record<WorkSize, string> = {
      '1-2_weeks': '1-2 weeks',
      '3-8_weeks': '3-8 weeks',
      '9-26_weeks': '2-6 months',
      '27-52_weeks': '6-12 months',
      '52+_weeks': '1+ year',
    };
    parts.push(`effort: ${sizeLabels[inputs.workSize]}`);
  }
  
  if (inputs.impactSurface) {
    const impactLabels: Record<ImpactSurface, string> = {
      'one_screen': 'one screen',
      'one_workflow': 'one workflow',
      'multiple_workflows': 'multiple workflows',
      'multiple_products': 'multiple products',
      'company_wide': 'company-wide',
    };
    parts.push(`impact: ${impactLabels[inputs.impactSurface]}`);
  }
  
  if (parts.length === 0) {
    return `Defaulted to Level ${level} (${levelNames[level]}) - no dimensions specified.`;
  }
  
  return `Level ${level} (${levelNames[level]}) based on ${parts.join(', ')}.`;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if an item has complete scoping inputs
 */
export function hasScopingInputs(inputs: AltitudeScopingInputs | undefined): boolean {
  if (!inputs) return false;
  return !!(inputs.timeHorizon || inputs.workSize || inputs.impactSurface);
}

/**
 * Check if all three dimensions are specified
 */
export function hasCompleteScopingInputs(inputs: AltitudeScopingInputs | undefined): boolean {
  if (!inputs) return false;
  return !!(inputs.timeHorizon && inputs.workSize && inputs.impactSurface);
}

/**
 * Get the count of specified dimensions (0-3)
 */
export function getScopingInputCount(inputs: AltitudeScopingInputs | undefined): number {
  if (!inputs) return 0;
  return [inputs.timeHorizon, inputs.workSize, inputs.impactSurface].filter(Boolean).length;
}

// ============================================================================
// Deprecated Functions (for backward compatibility during migration)
// ============================================================================

import type { ItemAltitude } from '../types/prioritization/itemLifecycle';
import { LEGACY_ALTITUDE_TO_LEVEL } from '../types/prioritization/itemLifecycle';

/**
 * @deprecated Use computeAltitudeLevel instead
 */
export function predictAltitude(inputs: AltitudeScopingInputs): { 
  predictedAltitude: AltitudeLevel; 
  confidence: 'high' | 'medium' | 'low'; 
  reasoning: string;
} {
  const result = computeAltitude(inputs);
  return {
    predictedAltitude: result.level,
    confidence: result.confidence,
    reasoning: result.reasoning,
  };
}

/**
 * @deprecated Altitude is now computed, not checked for consistency
 */
export function checkAltitudeConsistency(
  declared: AltitudeLevel,
  computed: AltitudeLevel
): {
  isConsistent: boolean;
  declaredAltitude: AltitudeLevel;
  predictedAltitude: AltitudeLevel;
  warning?: string;
  suggestedAltitude: AltitudeLevel;
} {
  const gap = Math.abs(declared - computed);
  
  if (gap <= 1) {
    return {
      isConsistent: true,
      declaredAltitude: declared,
      predictedAltitude: computed,
      suggestedAltitude: declared,
    };
  }
  
  const direction = declared > computed ? 'higher' : 'lower';
  const warning = `Declared level ${declared} is ${gap} levels ${direction} than computed level ${computed}.`;
  
  return {
    isConsistent: false,
    declaredAltitude: declared,
    predictedAltitude: computed,
    warning,
    suggestedAltitude: computed,
  };
}

/**
 * Convert legacy altitude label to new level
 */
export function legacyAltitudeToLevel(altitude: ItemAltitude | undefined): AltitudeLevel | undefined {
  if (!altitude) return undefined;
  return LEGACY_ALTITUDE_TO_LEVEL[altitude];
}
